import * as React from "react"

export const MOTION_STORAGE_KEY = "wf-motion-preference"
export const MOTION_MODES = ["auto", "reduced", "full"]

function isValidMotionMode(value) {
  return MOTION_MODES.includes(value)
}

export function readMotionPreference() {
  if (typeof window === "undefined") return "auto"

  try {
    const stored = window.localStorage.getItem(MOTION_STORAGE_KEY)
    return isValidMotionMode(stored) ? stored : "auto"
  } catch (_) {
    return "auto"
  }
}

export function getSystemReducedMotion() {
  if (typeof window === "undefined" || !window.matchMedia) return false
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches
}

export function resolveEffectiveMotion(mode) {
  if (mode === "reduced") return "reduced"
  if (mode === "full") return "full"
  return getSystemReducedMotion() ? "reduced" : "full"
}

export function applyMotionPreference(mode) {
  if (typeof document === "undefined") return
  const safeMode = isValidMotionMode(mode) ? mode : "auto"
  const effectiveMotion = resolveEffectiveMotion(safeMode)
  document.documentElement.dataset.motion = safeMode
  document.documentElement.dataset.motionEffective = effectiveMotion
  window.dispatchEvent(
    new CustomEvent("wf:motion-change", {
      detail: { mode: safeMode, effectiveMotion },
    })
  )
}

export function writeMotionPreference(mode) {
  const safeMode = isValidMotionMode(mode) ? mode : "auto"
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(MOTION_STORAGE_KEY, safeMode)
    } catch (_) {
      // localStorage can be unavailable in private or restricted contexts.
    }
  }
  applyMotionPreference(safeMode)
}

export function useMotionPreference() {
  const [mode, setModeState] = React.useState(readMotionPreference)
  const [effectiveMotion, setEffectiveMotion] = React.useState(() =>
    resolveEffectiveMotion(readMotionPreference())
  )

  React.useEffect(() => {
    applyMotionPreference(mode)

    const sync = event => {
      const nextMode = event?.detail?.mode || readMotionPreference()
      const nextEffectiveMotion =
        event?.detail?.effectiveMotion || resolveEffectiveMotion(nextMode)
      setModeState(nextMode)
      setEffectiveMotion(nextEffectiveMotion)
    }

    const mediaQuery = window.matchMedia?.("(prefers-reduced-motion: reduce)")
    const handleSystemChange = () => applyMotionPreference(readMotionPreference())

    window.addEventListener("wf:motion-change", sync)
    if (mediaQuery?.addEventListener) {
      mediaQuery.addEventListener("change", handleSystemChange)
    } else if (mediaQuery?.addListener) {
      mediaQuery.addListener(handleSystemChange)
    }

    return () => {
      window.removeEventListener("wf:motion-change", sync)
      if (mediaQuery?.removeEventListener) {
        mediaQuery.removeEventListener("change", handleSystemChange)
      } else if (mediaQuery?.removeListener) {
        mediaQuery.removeListener(handleSystemChange)
      }
    }
  }, [mode])

  const setMode = React.useCallback(nextMode => {
    const safeMode = isValidMotionMode(nextMode) ? nextMode : "auto"
    setModeState(safeMode)
    writeMotionPreference(safeMode)
  }, [])

  return {
    mode,
    effectiveMotion,
    reduceMotion: effectiveMotion === "reduced",
    setMode,
  }
}
